import { c as _c } from "react/compiler-runtime";
import { font_sizes } from '@hookdeck/theme';
import { Highlight, Language } from 'prism-react-renderer';
import { ClipboardEvent, Fragment, ReactNode, useEffect, useRef, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { copyTextToClipboard } from '../../utils/copy';
import { useToasts } from './Toast';
import { ClickableArea } from './base/Button';
import { StyledCard, StyledCardSection } from './base/Card';
import Icon from './base/Icon';
import Text from './base/Text';
import { Div, Span } from './helpers/StyledUtils';
const StyledContainer = styled(Div)`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
  background-color: ${({
  theme
}) => theme.colors.surface.base.variant_surface_2};
  border-radius: ${({
  theme
}) => theme.radius.normal};
  box-shadow: ${({
  theme
}) => theme.shadows.neutral.base};
`;
const StyledPre = styled(Div)<{
  $multiline: boolean;
}>`
  margin: 0;
  padding: ${({
  theme,
  $multiline
}) => `${theme.spacing($multiline ? 3 : 1.5)} ${theme.spacing(3)}`};
  padding-right: ${({
  theme
}) => theme.spacing(8)};
  overflow-x: auto;
  white-space: pre;
  font-family: 'JetBrains Mono', monospace;
  font-size: ${font_sizes.s.font_size}px;
  line-height: ${font_sizes.s.line_height}px;
  text-wrap: auto;
`;
const StyledCopyButton = styled.button<{
  $multiline: boolean;
}>`
  position: absolute;
  top: ${({
  theme,
  $multiline
}) => theme.spacing($multiline ? 2 : 1)};
  right: ${({
  theme
}) => theme.spacing(2)};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  outline: none;
  cursor: pointer;
  background: transparent;
  border-radius: ${({
  theme
}) => theme.radius.normal};
  padding: ${({
  theme
}) => theme.spacing(1)};

  &:hover {
    background-color: ${({
  theme
}) => theme.colors.surface.base.hover.neutral};
    box-shadow: ${({
  theme
}) => theme.shadows.neutral.base};
  }

  &:focus-visible {
    box-shadow: ${({
  theme
}) => theme.shadows.neutral.focused};
  }
`;
const StyledLine = styled(Div)`
  display: block;
`;
const StyledRevealToken = styled(Span)<{
  $interactive: boolean;
}>`
  border-radius: ${({
  theme
}) => theme.radius.xsmall};
  ${({
  $interactive,
  theme
}) => $interactive ? css`
          cursor: pointer;
          text-decoration: underline dotted;
          text-underline-offset: 2px;
          text-decoration-thickness: 1px;

          &:hover,
          &:focus-visible {
            background-color: ${theme.colors.surface.container.primary};
            box-shadow: 0 0 0 1px ${theme.colors.outline.primary};
            outline: none;
          }
        ` : ''}
`;
const StyledUnderlinedToken = styled(Span)`
  text-decoration: underline dotted;
  text-underline-offset: 2px;
  text-decoration-thickness: 1px;
`;
interface MaskableValue {
  placeholder: string;
  masked_value: string;
  full_value: string;
  reveal_on_hover?: boolean;
}
interface UnderlinedValue {
  placeholder: string;
  value: string;
}
interface Props {
  code: string;
  language: Language;
  maskable_value?: MaskableValue;
  underlined_values?: UnderlinedValue[];
  /** Fires after a successful copy from this snippet (copy button or maskable click-to-copy). */
  onCopySuccess?: () => void;
}
interface CodeSnippetWithFormProps extends Props {
  section_title: string;
  children: ReactNode;
  default_open?: boolean;
}
const replaceFirst = (input: string, target: string, replacement: string) => {
  const target_index = input.indexOf(target);
  if (target_index === -1) {
    return input;
  }
  return input.slice(0, target_index) + replacement + input.slice(target_index + target.length);
};
const tokenContainsValue = (content: string | {
  content: string;
}[], value: string): boolean => {
  if (typeof content === 'string') {
    return content.includes(value);
  }
  return content.some(item => tokenContainsValue(item.content, value));
};
const replaceUnderlinedValues = (input: string, underlined_values?: UnderlinedValue[]) => {
  if (!underlined_values?.length) {
    return input;
  }
  return underlined_values.reduce((output, underlined_value) => {
    return replaceFirst(output, underlined_value.placeholder, underlined_value.value);
  }, input);
};
const elementContainsNode = (element: HTMLElement | null, node: Node | null): boolean => {
  return Boolean(element && node && (element === node || element.contains(node)));
};
const getSelectedTextWithinElement = (element: HTMLElement | null): string => {
  if (!element || typeof window === 'undefined') {
    return '';
  }
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
    return '';
  }
  if (!elementContainsNode(element, selection.anchorNode) || !elementContainsNode(element, selection.focusNode)) {
    return '';
  }
  return selection.toString();
};
const CodeSnippet: React.FC<Props> = t0 => {
  const $ = _c(76);
  const {
    code,
    language,
    maskable_value,
    underlined_values,
    onCopySuccess
  } = t0;
  const theme = useTheme();
  const {
    addToast
  } = useToasts();
  const [is_hover_revealed, set_is_hover_revealed] = useState(false);
  const [is_selection_revealed, set_is_selection_revealed] = useState(false);
  let t1;
  if ($[0] !== addToast || $[1] !== onCopySuccess) {
    t1 = async text => {
      const ok = await copyTextToClipboard(text);
      if (ok) {
        addToast("success", "Copied to clipboard");
        onCopySuccess?.();
      }
    };
    $[0] = addToast;
    $[1] = onCopySuccess;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const copy_to_clipboard = t1;
  const pre_ref = useRef(null);
  const can_reveal = Boolean(maskable_value?.reveal_on_hover ?? true);
  const is_revealed = can_reveal && (is_hover_revealed || is_selection_revealed);
  const displayed_value = maskable_value ? is_revealed && can_reveal ? maskable_value.full_value : maskable_value.masked_value : undefined;
  let copyable_code;
  let displayed_code;
  let t2;
  if ($[3] !== code || $[4] !== displayed_value || $[5] !== maskable_value || $[6] !== underlined_values) {
    let t3;
    bb0: {
      const code_with_underlined_values = replaceUnderlinedValues(code, underlined_values);
      if (!maskable_value || !displayed_value) {
        t3 = code_with_underlined_values;
        break bb0;
      }
      t3 = replaceFirst(code_with_underlined_values, maskable_value.placeholder, displayed_value);
    }
    displayed_code = t3;
    let t4;
    if ($[10] !== code || $[11] !== maskable_value || $[12] !== underlined_values) {
      bb1: {
        const code_with_underlined_values_0 = replaceUnderlinedValues(code, underlined_values);
        if (!maskable_value) {
          t4 = code_with_underlined_values_0;
          break bb1;
        }
        t4 = replaceFirst(code_with_underlined_values_0, maskable_value.placeholder, maskable_value.full_value);
      }
      $[10] = code;
      $[11] = maskable_value;
      $[12] = underlined_values;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    copyable_code = t4;
    t2 = displayed_code.includes("\n");
    $[3] = code;
    $[4] = displayed_value;
    $[5] = maskable_value;
    $[6] = underlined_values;
    $[7] = copyable_code;
    $[8] = displayed_code;
    $[9] = t2;
  } else {
    copyable_code = $[7];
    displayed_code = $[8];
    t2 = $[9];
  }
  const is_multiline = t2;
  let t3;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => getSelectedTextWithinElement(pre_ref.current);
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const get_selected_text = t3;
  let t4;
  if ($[15] !== can_reveal || $[16] !== maskable_value) {
    t4 = () => {
      if (!can_reveal || !maskable_value) {
        set_is_selection_revealed(false);
        return;
      }
      const selected_text = get_selected_text();
      set_is_selection_revealed(selected_text.includes(maskable_value.masked_value) || selected_text.includes(maskable_value.full_value));
    };
    $[15] = can_reveal;
    $[16] = maskable_value;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const sync_selection_reveal = t4;
  let t5;
  if ($[18] !== maskable_value) {
    t5 = event => {
      if (!maskable_value) {
        return;
      }
      const selected_text_0 = get_selected_text();
      if (!selected_text_0) {
        return;
      }
      const copied_text = selected_text_0.split(maskable_value.masked_value).join(maskable_value.full_value);
      if (copied_text === selected_text_0) {
        return;
      }
      event.preventDefault();
      event.clipboardData.setData("text/plain", copied_text);
    };
    $[18] = maskable_value;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const handle_copy = t5;
  let t6;
  let t7;
  if ($[20] !== sync_selection_reveal) {
    t6 = () => {
      if (typeof document === "undefined") {
        return;
      }
      document.addEventListener("selectionchange", sync_selection_reveal);
      return () => document.removeEventListener("selectionchange", sync_selection_reveal);
    };
    t7 = [sync_selection_reveal];
    $[20] = sync_selection_reveal;
    $[21] = t6;
    $[22] = t7;
  } else {
    t6 = $[21];
    t7 = $[22];
  }
  useEffect(t6, t7);
  let t8;
  if ($[23] !== theme.colors.on.neutral.primary_neutral) {
    t8 = {
      backgroundColor: "transparent",
      color: theme.colors.on.neutral.primary_neutral
    };
    $[23] = theme.colors.on.neutral.primary_neutral;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = ["comment", "prolog", "doctype", "cdata"];
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== theme.colors.on.neutral.secondary_neutral) {
    t10 = {
      types: t9,
      style: {
        color: theme.colors.on.neutral.secondary_neutral,
        fontStyle: "italic" as const
      }
    };
    $[26] = theme.colors.on.neutral.secondary_neutral;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = ["punctuation", "operator"];
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== theme.colors.on.neutral.tertiary_neutral) {
    t12 = {
      types: t11,
      style: {
        color: theme.colors.on.neutral.tertiary_neutral
      }
    };
    $[29] = theme.colors.on.neutral.tertiary_neutral;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = ["boolean", "number"];
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== theme.colors.on.code.number) {
    t14 = {
      types: t13,
      style: {
        color: theme.colors.on.code.number
      }
    };
    $[32] = theme.colors.on.code.number;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = ["string", "char", "attr-value", "inserted"];
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== theme.colors.on.code.string) {
    t16 = {
      types: t15,
      style: {
        color: theme.colors.on.code.string
      }
    };
    $[35] = theme.colors.on.code.string;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = ["keyword", "atrule"];
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== theme.colors.on.neutral.primary) {
    t18 = {
      types: t17,
      style: {
        color: theme.colors.on.neutral.primary
      }
    };
    $[38] = theme.colors.on.neutral.primary;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = ["function", "class-name", "builtin", "property"];
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== theme.colors.on.code.boolean) {
    t20 = {
      types: t19,
      style: {
        color: theme.colors.on.code.boolean
      }
    };
    $[41] = theme.colors.on.code.boolean;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== t10 || $[44] !== t12 || $[45] !== t14 || $[46] !== t16 || $[47] !== t18 || $[48] !== t20) {
    t21 = [t10, t12, t14, t16, t18, t20];
    $[43] = t10;
    $[44] = t12;
    $[45] = t14;
    $[46] = t16;
    $[47] = t18;
    $[48] = t20;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== t21 || $[51] !== t8) {
    t22 = {
      plain: t8,
      styles: t21
    };
    $[50] = t21;
    $[51] = t8;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  const prism_theme = t22;
  let t23;
  if ($[53] !== copy_to_clipboard || $[54] !== copyable_code) {
    t23 = () => copy_to_clipboard(copyable_code);
    $[53] = copy_to_clipboard;
    $[54] = copyable_code;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <Icon icon="copy" muted={true} small={true} />;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  let t25;
  if ($[57] !== is_multiline || $[58] !== t23) {
    t25 = <StyledCopyButton $multiline={is_multiline} type="button" aria-label="Copy code snippet" title="Copy code snippet" onClick={t23}>{t24}</StyledCopyButton>;
    $[57] = is_multiline;
    $[58] = t23;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] !== can_reveal || $[61] !== copy_to_clipboard || $[62] !== displayed_value || $[63] !== handle_copy || $[64] !== is_multiline || $[65] !== maskable_value || $[66] !== underlined_values) {
    t26 = t27 => {
      const {
        className,
        style,
        tokens,
        getLineProps,
        getTokenProps
      } = t27;
      return <StyledPre as="pre" ref={pre_ref} className={className} style={style} $multiline={is_multiline} onCopy={handle_copy}>{tokens.map((line, line_index) => {
          const line_props = getLineProps({
            line,
            key: line_index
          });
          return <StyledLine key={line_index} className={line_props.className} style={line_props.style}>{line.map((token, token_index) => {
              const token_props = getTokenProps({
                token,
                key: token_index
              });
              const token_children = typeof token_props.children === "string" ? token_props.children : String(token_props.children ?? "");
              const is_reveal_token = Boolean(displayed_value && tokenContainsValue(token.content, displayed_value));
              const matched_underlined_value = underlined_values?.find(underlined_value => tokenContainsValue(token.content, underlined_value.value));
              if (is_reveal_token) {
                const [before_value, ...after_values] = token_children.split(displayed_value ?? "");
                const after_value = after_values.join(displayed_value ?? "");
                return <Fragment key={token_index}>{before_value && <Span key={`${token_index}-before`} className={token_props.className} style={token_props.style}>{before_value}</Span>}<StyledRevealToken key={`${token_index}-value`} className={token_props.className} style={token_props.style} $interactive={can_reveal} role={can_reveal ? "button" : undefined} title={can_reveal ? "Click to copy API key" : undefined} aria-label={can_reveal ? "Copy API key" : undefined} tabIndex={can_reveal ? 0 : undefined} onMouseEnter={can_reveal ? () => set_is_hover_revealed(true) : undefined} onMouseLeave={can_reveal ? () => set_is_hover_revealed(false) : undefined} onFocus={can_reveal ? () => set_is_hover_revealed(true) : undefined} onBlur={can_reveal ? () => set_is_hover_revealed(false) : undefined} onClick={can_reveal && maskable_value ? () => copy_to_clipboard(maskable_value.full_value) : undefined} onKeyDown={can_reveal && maskable_value ? event_0 => {
                    if (event_0.key === "Enter" || event_0.key === " ") {
                      event_0.preventDefault();
                      copy_to_clipboard(maskable_value.full_value);
                    }
                  } : undefined}>{displayed_value}</StyledRevealToken>{after_value && <Span key={`${token_index}-after`} className={token_props.className} style={token_props.style}>{after_value}</Span>}</Fragment>;
              }
              if (matched_underlined_value) {
                const [before_value_0, ...after_values_0] = token_children.split(matched_underlined_value.value);
                const after_value_0 = after_values_0.join(matched_underlined_value.value);
                return <Fragment key={token_index}>{before_value_0 && <Span key={`${token_index}-before`} className={token_props.className} style={token_props.style}>{before_value_0}</Span>}<StyledUnderlinedToken key={`${token_index}-underlined`} className={token_props.className} style={token_props.style}>{matched_underlined_value.value}</StyledUnderlinedToken>{after_value_0 && <Span key={`${token_index}-after`} className={token_props.className} style={token_props.style}>{after_value_0}</Span>}</Fragment>;
              }
              return <Span key={token_index} className={token_props.className} style={token_props.style}>{token_props.children}</Span>;
            })}</StyledLine>;
        })}</StyledPre>;
    };
    $[60] = can_reveal;
    $[61] = copy_to_clipboard;
    $[62] = displayed_value;
    $[63] = handle_copy;
    $[64] = is_multiline;
    $[65] = maskable_value;
    $[66] = underlined_values;
    $[67] = t26;
  } else {
    t26 = $[67];
  }
  let t27;
  if ($[68] !== displayed_code || $[69] !== language || $[70] !== prism_theme || $[71] !== t26) {
    t27 = <Highlight code={displayed_code} language={language} theme={prism_theme}>{t26}</Highlight>;
    $[68] = displayed_code;
    $[69] = language;
    $[70] = prism_theme;
    $[71] = t26;
    $[72] = t27;
  } else {
    t27 = $[72];
  }
  let t28;
  if ($[73] !== t25 || $[74] !== t27) {
    t28 = <StyledContainer>{t25}{t27}</StyledContainer>;
    $[73] = t25;
    $[74] = t27;
    $[75] = t28;
  } else {
    t28 = $[75];
  }
  return t28;
};
export const CodeSnippetWithForm: React.FC<CodeSnippetWithFormProps> = t0 => {
  const $ = _c(30);
  let children;
  let code_snippet_props;
  let section_title;
  let t1;
  if ($[0] !== t0) {
    ({
      section_title,
      children,
      default_open: t1,
      ...code_snippet_props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = code_snippet_props;
    $[3] = section_title;
    $[4] = t1;
  } else {
    children = $[1];
    code_snippet_props = $[2];
    section_title = $[3];
    t1 = $[4];
  }
  const default_open = t1 === undefined ? false : t1;
  const [is_open, setIsOpen] = useState(default_open);
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      position: "relative"
    };
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      zIndex: 1,
      position: "relative"
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== code_snippet_props) {
    t4 = <Div style={t3}><CodeSnippet {...code_snippet_props} /></Div>;
    $[7] = code_snippet_props;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      overflow: "hidden"
    };
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  let t7;
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      align: "center",
      justify: "space-between"
    };
    t7 = {
      x: 3,
      t: 3,
      b: 2
    };
    t8 = {
      t: -1
    };
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
  } else {
    t6 = $[10];
    t7 = $[11];
    t8 = $[12];
  }
  let t9;
  if ($[13] !== is_open) {
    t9 = () => setIsOpen(!is_open);
    $[13] = is_open;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== section_title) {
    t10 = <Text semi={true} muted={true} size="s">{section_title}</Text>;
    $[15] = section_title;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const t11 = is_open ? "chevron_up" : "chevron_down";
  let t12;
  if ($[17] !== t11) {
    t12 = <Icon icon={t11} />;
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== t10 || $[20] !== t12 || $[21] !== t9) {
    t13 = <StyledCardSection borderless={true} style={t5}><ClickableArea flex={t6} p={t7} m={t8} onClick={t9}>{t10}{t12}</ClickableArea></StyledCardSection>;
    $[19] = t10;
    $[20] = t12;
    $[21] = t9;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== children || $[24] !== is_open) {
    t14 = is_open && <StyledCardSection p={{
      x: 3,
      y: 3
    }}>{children}</StyledCardSection>;
    $[23] = children;
    $[24] = is_open;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t13 || $[27] !== t14 || $[28] !== t4) {
    t15 = <StyledCard style={t2}>{t4}{t13}{t14}</StyledCard>;
    $[26] = t13;
    $[27] = t14;
    $[28] = t4;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  return t15;
};
export default CodeSnippet;